/**
 * condition_data をセル表示可能な形式に変換
 * csvData またはフォームデータ（dental_history 等）をテーブル形式に
 */

import {
  compareConditionItems,
  normalizeConditionCategories,
  NormalizedConditionCategory,
} from "./conditionTableNormalizer";

export interface DisplayCell {
  rowIndex: number;
  colIndex: number;
  header: string;
  value: string;
}

export interface CellGrid {
  headers: string[];
  rows: { rowIndex: number; cells: DisplayCell[] }[];
}

const FLATTEN_SKIP_KEYS = new Set(['csvData', 'categoryTableV2', 'categoryDefinitions']);

export function hasConditionCategoryGrid(conditionData: Record<string, unknown> | undefined): boolean {
  if (!conditionData || typeof conditionData !== 'object') return false;
  return (
    conditionData.conditionTableVersion === 3 ||
    Array.isArray(conditionData.categoryDefinitions) ||
    !!conditionData.categoryTableV2 ||
    !!conditionData.csvData
  );
}

export function applyCellGridEditsToV3ConditionData(
  conditionData: Record<string, unknown>,
  grid: CellGrid,
  cells: Record<string, string>,
): Record<string, unknown> {
  const categoryDefinitions = conditionData.categoryDefinitions;
  if (!Array.isArray(categoryDefinitions)) return conditionData;

  const rowIndexByCategoryId = new Map<string, number>();
  for (const row of grid.rows) {
    const idCell = row.cells.find((cell) => cell.header === 'ID');
    if (idCell?.value) rowIndexByCategoryId.set(idCell.value, row.rowIndex);
  }

  const hasCell = (rowIndex: number, header: string) =>
    Object.prototype.hasOwnProperty.call(cells, `${rowIndex}::${header}`);
  const getCell = (rowIndex: number, header: string) => cells[`${rowIndex}::${header}`];

  return {
    ...conditionData,
    categoryDefinitions: categoryDefinitions.map((rawCategory) => {
      if (!rawCategory || typeof rawCategory !== 'object' || Array.isArray(rawCategory)) {
        return rawCategory;
      }

      const category = rawCategory as Record<string, unknown>;
      const categoryId = typeof category.categoryId === 'string' ? category.categoryId : '';
      const rowIndex = rowIndexByCategoryId.get(categoryId);
      if (rowIndex === undefined) return category;

      const rawItems = Array.isArray(category.items) ? category.items : [];
      const updatedCategory: Record<string, unknown> = {
        ...category,
        items: rawItems.map((rawItem) => {
          if (!rawItem || typeof rawItem !== 'object' || Array.isArray(rawItem)) return rawItem;
          const item = rawItem as Record<string, unknown>;
          const columnKey = typeof item.columnKey === 'string' ? item.columnKey : '';
          if (!columnKey || !hasCell(rowIndex, columnKey)) return item;
          return {
            ...item,
            text: getCell(rowIndex, columnKey),
          };
        }),
      };

      if (hasCell(rowIndex, '備考')) {
        const notes = getCell(rowIndex, '備考');
        if (notes.trim().length > 0) {
          updatedCategory.notes = notes;
        } else {
          delete updatedCategory.notes;
        }
      }

      return updatedCategory;
    }),
  };
}

function flattenObject(
  obj: Record<string, unknown>,
  prefix = ''
): Array<{ path: string; value: string }> {
  const result: Array<{ path: string; value: string }> = [];
  for (const [key, value] of Object.entries(obj)) {
    if (FLATTEN_SKIP_KEYS.has(key)) continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if (value === null || value === undefined) {
      result.push({ path, value: '' });
    } else if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      const nested = flattenObject(value as Record<string, unknown>, path);
      result.push(...nested);
    } else if (Array.isArray(value)) {
      result.push({ path, value: JSON.stringify(value) });
    } else {
      result.push({ path, value: String(value) });
    }
  }
  return result;
}

/**
 * condition_data をセルグリッドに変換
 */
export function conditionDataToCellGrid(conditionData: Record<string, unknown> | undefined): CellGrid | null {
  if (!conditionData || typeof conditionData !== 'object') return null;

  // V3 categoryDefinitions / categoryTableV2形式（カテゴリ × item）
  if (conditionData.conditionTableVersion === 3 || conditionData.categoryDefinitions || conditionData.categoryTableV2) {
    const normalized = normalizeConditionCategories(conditionData);
    const grid = normalizedCategoriesToGrid(normalized);
    if (grid) return grid;
  }

  // csvData形式
  const csvData = conditionData.csvData as
    | { headers?: string[]; rows?: Array<Record<string, unknown>> }
    | undefined;

  if (csvData?.headers && Array.isArray(csvData.headers) && csvData.rows && Array.isArray(csvData.rows)) {
    const headers = csvData.headers;
    const rows = csvData.rows.map((row, rowIdx) => ({
      rowIndex: rowIdx,
      cells: headers.map((header, colIdx) => ({
        rowIndex: rowIdx,
        colIndex: colIdx,
        header,
        value: String(row[header] ?? ''),
      })),
    }));
    return { headers, rows };
  }

  // categoryTable形式（C1〜C16 × Lv1〜Lv6）
  const categoryTable = conditionData.categoryTable as
    | Record<string, Record<string, string>>
    | undefined;

  if (categoryTable && typeof categoryTable === 'object') {
    const lvKeys = new Set<string>();
    for (const cat of Object.values(categoryTable)) {
      if (cat && typeof cat === 'object') {
        for (const key of Object.keys(cat)) {
          if (key.startsWith('Lv')) lvKeys.add(key);
        }
      }
    }
    const sortedLvKeys = Array.from(lvKeys).sort((a, b) => {
      const na = parseInt(a.replace('Lv', ''), 10);
      const nb = parseInt(b.replace('Lv', ''), 10);
      return na - nb;
    });

    const headers = ['ID', 'カテゴリ', ...sortedLvKeys, '備考'];
    const rows = Object.entries(categoryTable).map(([catId, catData], rowIdx) => {
      const cells: DisplayCell[] = [];
      let colIdx = 0;
      cells.push({ rowIndex: rowIdx, colIndex: colIdx++, header: 'ID', value: catId });
      cells.push({ rowIndex: rowIdx, colIndex: colIdx++, header: 'カテゴリ', value: catData?.category ?? '' });
      for (const lv of sortedLvKeys) {
        cells.push({ rowIndex: rowIdx, colIndex: colIdx++, header: lv, value: catData?.[lv] ?? '' });
      }
      cells.push({ rowIndex: rowIdx, colIndex: colIdx++, header: '備考', value: catData?.notes ?? '' });
      return { rowIndex: rowIdx, cells };
    });
    return { headers, rows };
  }

  // フォールバック：フラット形式
  const items = flattenObject(conditionData);
  if (items.length === 0) return null;

  const headers = ['項目', '値'];
  const rows = items.map((item, rowIdx) => ({
    rowIndex: rowIdx,
    cells: [
      { rowIndex: rowIdx, colIndex: 0, header: '項目', value: item.path },
      { rowIndex: rowIdx, colIndex: 1, header: '値', value: item.value },
    ],
  }));
  return { headers, rows };
}

function normalizedCategoriesToGrid(categories: NormalizedConditionCategory[]): CellGrid | null {
  if (categories.length === 0) return null;

  // 全カテゴリの items をマージしてからソートし、columnKey で重複排除する。
  // カテゴリ単位でソート→Map.has で重複除外する方式だと、最初のカテゴリに
  // 欠けている columnKey (例: C1 に Lv2_数量 が無い等) が後ろに回されてしまう。
  const allItems = categories.flatMap((c) => c.items);
  const itemColumns = new Map<string, string>();
  for (const item of allItems.slice().sort(compareConditionItems)) {
    if (!itemColumns.has(item.columnKey)) {
      itemColumns.set(item.columnKey, item.label);
    }
  }

  const itemHeaders = Array.from(itemColumns.keys());
  if (itemHeaders.length === 0) return null;

  const headers = ['ID', 'カテゴリ', ...itemHeaders, '備考'];
  const rows = categories.map((category, rowIdx) => {
    const cells: DisplayCell[] = [];
    let colIdx = 0;
    cells.push({ rowIndex: rowIdx, colIndex: colIdx++, header: 'ID', value: category.categoryId });
    cells.push({ rowIndex: rowIdx, colIndex: colIdx++, header: 'カテゴリ', value: category.categoryName });

    for (const header of itemHeaders) {
      const item = category.items.find((candidate) => candidate.columnKey === header);
      cells.push({ rowIndex: rowIdx, colIndex: colIdx++, header, value: item?.text ?? '' });
    }

    cells.push({ rowIndex: rowIdx, colIndex: colIdx++, header: '備考', value: category.notes ?? '' });
    return { rowIndex: rowIdx, cells };
  });

  return { headers, rows };
}

/**
 * 選択セルからLLM用のテキストを生成
 */
export function selectedCellsToSummary(
  cells: DisplayCell[],
  grid: CellGrid
): string {
  if (cells.length === 0) return '';
  const lines = ['【選択された健康心理条件】'];
  const selectedSet = new Set(cells.map((c) => `${c.rowIndex}-${c.colIndex}`));
  for (const row of grid.rows) {
    const rowCells = row.cells.filter((c) =>
      selectedSet.has(`${c.rowIndex}-${c.colIndex}`)
    );
    if (rowCells.length > 0) {
      const parts = rowCells.map((c) => `${c.header}: ${c.value}`).join(' | ');
      lines.push(parts);
    }
  }
  return lines.join('\n');
}
